import { useState, useRef, useEffect } from 'react'
import { pushTemplatesApi } from '../services/api'
import { Button, Badge } from './common'
import { formatVariableForPrompt } from '../utils/templateVariableHelpers'

const DEFAULT_ICONS = {
  Announcement: 'megaphone',
  AnnouncementReminder: 'megaphone',
  DeliveryNotification: 'package',
  AmenityBookingConfirmation: 'dumbbell',
  AmenityBookingDecline: 'dumbbell',
  AmenityBookingCancellation: 'dumbbell',
  AdminAmenityBookingRequest: 'dumbbell',
  ParkingBookingConfirmation: 'car',
  ParkingBookingDecline: 'car',
  ParkingBookingCancellation: 'car',
  ParkingCheckInExpired: 'car',
  AdminParkingBookingRequest: 'car',
  TaskCreated: 'wrench',
  TaskAssigned: 'wrench',
  TaskEdited: 'wrench',
  TaskCommentAdded: 'message-square',
}

const PushBatchGenerateModal = ({ templateTypes, existingTemplates, variablesMap, onClose, onComplete }) => {
  const [step, setStep] = useState('configure')
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [progressItems, setProgressItems] = useState([])
  const [summary, setSummary] = useState(null)
  const abortedRef = useRef(false)

  const existingTypeValues = new Set(
    (existingTemplates || []).map(t => parseInt(t.templateType))
  )

  const handleSelectAll = (checked) => {
    setSelectAll(checked)
    setSelectedTypes(checked ? templateTypes.map(t => t.name) : [])
  }

  const handleToggleType = (name) => {
    setSelectedTypes(prev =>
      prev.includes(name)
        ? prev.filter(n => n !== name)
        : [...prev, name]
    )
  }

  useEffect(() => {
    setSelectAll(selectedTypes.length === templateTypes.length)
  }, [selectedTypes, templateTypes])

  const handleStart = async () => {
    setStep('progress')
    abortedRef.current = false

    const items = selectedTypes.map(name => ({
      name,
      status: 'pending',
      error: null
    }))
    setProgressItems(items)

    let succeeded = 0
    let failed = 0

    for (let i = 0; i < selectedTypes.length; i++) {
      if (abortedRef.current) break

      const typeName = selectedTypes[i]
      const type = templateTypes.find(t => t.name === typeName)
      if (!type) {
        setProgressItems(prev => prev.map((item, idx) =>
          idx === i ? { ...item, status: 'error', error: 'Type not found' } : item
        ))
        failed++
        continue
      }

      // Mark as processing
      setProgressItems(prev => prev.map((item, idx) =>
        idx === i ? { ...item, status: 'processing' } : item
      ))

      try {
        // Build prompt with available variables — strongly enforce required vars in body
        const vars = variablesMap[type.value] || []
        const requiredVars = vars.filter(v => v.isRequired)
        const optionalVars = vars.filter(v => !v.isRequired)
        const requiredList = requiredVars.map(formatVariableForPrompt).join(', ')
        const optionalList = optionalVars.map(formatVariableForPrompt).join(', ')

        let prompt = `Generate a push notification template for "${typeName}". Title under 60 chars, body under 200 chars.`
        if (requiredVars.length > 0) {
          prompt += `\n\nREQUIRED variables — you MUST include ALL of these in the messageBody: ${requiredList}`
        }
        if (optionalVars.length > 0) {
          prompt += `\nOptional variables (use if they fit naturally): ${optionalList}`
        }

        // Generate with AI
        const aiResponse = await pushTemplatesApi.generateAI({
          prompt,
          templateType: type.value
        })

        if (abortedRef.current) break

        const title = aiResponse.data?.title
        let messageBody = aiResponse.data?.messageBody
        if (!title || !messageBody) throw new Error('AI returned empty content')

        // Local validation: ensure all required variables appear in title + body combined
        if (requiredVars.length > 0) {
          const combined = (title + ' ' + messageBody)
          const missingVars = requiredVars.filter(v => !combined.includes(`{{${v.name}}}`))
          if (missingVars.length > 0) {
            // Append missing required variables as a fallback line
            const fallback = missingVars.map(v => `{{${v.name}}}`).join(', ')
            messageBody = messageBody.trimEnd() + ` — ${fallback}`
          }
        }

        // Save — create or update
        const isExisting = existingTypeValues.has(type.value)
        const templateData = {
          templateType: type.value,
          templateName: typeName.replace(/([A-Z])/g, ' $1').trim(),
          title,
          messageBody,
          iconName: DEFAULT_ICONS[typeName] || null
        }

        if (isExisting) {
          await pushTemplatesApi.update(templateData)
        } else {
          await pushTemplatesApi.create(templateData)
        }

        succeeded++
        setProgressItems(prev => prev.map((item, idx) =>
          idx === i ? { ...item, status: 'success' } : item
        ))
      } catch (err) {
        failed++
        setProgressItems(prev => prev.map((item, idx) =>
          idx === i ? { ...item, status: 'error', error: err.response?.data?.detail || err.message } : item
        ))
      }
    }

    const cancelled = abortedRef.current
      ? selectedTypes.length - succeeded - failed - selectedTypes.slice(0, succeeded + failed).length
      : 0

    setSummary({
      total: selectedTypes.length,
      succeeded,
      failed,
      cancelled: Math.max(0, selectedTypes.length - succeeded - failed)
    })
    setStep('summary')
  }

  const handleCancel = () => {
    abortedRef.current = true
  }

  const handleClose = () => {
    abortedRef.current = true
    if (onComplete) onComplete()
    onClose()
  }

  const completedCount = progressItems.filter(i => i.status === 'success' || i.status === 'error').length

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl mx-4" style={{ maxHeight: '80vh' }}>
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'configure' && 'Batch Generate Push Templates'}
            {step === 'progress' && `Generating Templates (${completedCount}/${selectedTypes.length})`}
            {step === 'summary' && 'Batch Complete'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {/* Step 1: Configure */}
          {step === 'configure' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Select push notification template types to generate with AI. Existing templates will be overwritten.
              </p>

              {/* Template Selection */}
              <div>
                <label className="flex items-center gap-3 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({templateTypes.length})
                  </span>
                </label>
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {templateTypes.map(type => (
                    <label key={type.name} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type.name)}
                        onChange={() => handleToggleType(type.name)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{type.name}</span>
                      {existingTypeValues.has(type.value) ? (
                        <Badge variant="warning" size="small">existing</Badge>
                      ) : (
                        <Badge variant="primary" size="small">new</Badge>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Progress */}
          {step === 'progress' && (
            <div className="space-y-2">
              {progressItems.map((item, index) => (
                <div key={item.name} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  {item.status === 'pending' && (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}
                  {item.status === 'processing' && (
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  )}
                  {item.status === 'success' && (
                    <span className="text-green-500 flex-shrink-0">&#10003;</span>
                  )}
                  {item.status === 'error' && (
                    <span className="text-red-500 flex-shrink-0">&#10005;</span>
                  )}
                  <span className="text-sm text-gray-700 flex-1">{item.name}</span>
                  {item.status === 'error' && item.error && (
                    <span className="text-xs text-red-500 max-w-48 truncate" title={item.error}>{item.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 'summary' && summary && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${
                summary.failed > 0 || summary.cancelled > 0
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-green-50 border border-green-200'
              }`}>
                <p className="text-sm font-medium">
                  {summary.cancelled > 0
                    ? 'Batch generation cancelled'
                    : summary.failed > 0
                      ? 'Batch generation completed with errors'
                      : 'Batch generation complete'}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{summary.succeeded}</div>
                  <div className="text-xs text-gray-500">Succeeded</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{summary.failed + summary.cancelled}</div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              </div>
              {progressItems.some(i => i.status === 'error') && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Failed Templates:</p>
                  <div className="space-y-1">
                    {progressItems.filter(i => i.status === 'error').map(item => (
                      <div key={item.name} className="text-sm p-2 bg-red-50 rounded border border-red-100">
                        <span className="font-medium">{item.name}</span>: <span className="text-red-600">{item.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          {step === 'configure' && (
            <>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleStart}
                disabled={selectedTypes.length === 0}
              >
                Start ({selectedTypes.length} templates)
              </Button>
            </>
          )}
          {step === 'progress' && (
            <Button variant="danger" onClick={handleCancel}>Cancel</Button>
          )}
          {step === 'summary' && (
            <Button variant="primary" onClick={handleClose}>Close</Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PushBatchGenerateModal
